// src/pages/BulkListingGenerator/components/SkuSidebar.js
import React from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import styles from './BulkFormHelper.module.css';

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M7 0C3.15098 0 0 3.15083 0 7C0 10.8492 3.15083 14 7 14C10.8492 14 14 10.8492 14 7C14 3.15083 10.8492 0 7 0ZM9.55375 4.49815C9.94345 4.05763 10.613 4.64947 10.2233 5.09058L6.32807 9.50109C6.15922 9.69155 5.86417 9.70324 5.68189 9.52504L3.80002 7.68577C3.37761 7.27387 4.00276 6.63352 4.42401 7.04425L5.96996 8.55625L9.55375 4.49815Z" fill="#96C7FB"/>
    </svg>
);

const BackIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.6667 9.16671H6.52504L11.1834 4.50837L10 3.33337L3.33337 10L10 16.6667L11.175 15.4917L6.52504 10.8334H16.6667V9.16671Z" fill="#505050"/>
    </svg>
);

const SkuCard = React.memo(({ item, isSelected, onClick, getSkuThumbnail, isChild }) => {
    const thumbnail = getSkuThumbnail(item.sku);
    return (
    <div
        className={`${styles.skuCard} ${
            isSelected ? styles.skuCardSelected : isChild ? styles.skuCardChild : ''
        }`}
        onClick={onClick}
    >
        <div className={styles.skuCardImage}>
            {thumbnail ? (
                <img src={thumbnail} alt={item.sku} />
            ) : (
                <div className={styles.skuCardImagePlaceholder} />
            )}
            {isSelected && (
                <div className={styles.skuCardCheck}><CheckIcon /></div>
            )}
        </div>
        <div className={styles.skuCardContent}>
            <div className={styles.skuCardTitle}>{item.title}</div>
            <div className={styles.skuCardBadge}>SKU ID : {item.sku}</div>
        </div>
    </div>
    );
});

const SkuSidebar = ({
    skuList,
    selectedSku,
    selectSku,
    setFormStep,
    parentSkus,
    childSkus,
    expandedParentSku,
    setExpandedParentSku,
    getSkuThumbnail,
    isLoadingImages,
    onBack,
}) => {
    const handleSkuClick = (sku) => {
        selectSku(sku);
        setFormStep(2);
    };

    return (
        <div className={styles.skuSidebar}>
            <div className={styles.sidebarHeader}>
                <button type="button" className={styles.backButton} onClick={onBack} aria-label="Back">
                    <BackIcon />
                </button>
                <div className={styles.skuHeaderContent}>
                    <div className={styles.skuHeaderPill}>
                        <span className={styles.sidebarTitle}>SKU ID</span>
                    </div>
                </div>
            </div>

            <div className={styles.skuListContainer}>
                {/* Parents first; clicking a parent expands its children */}
                {parentSkus.length > 0 && (
                    <>
                        <div className={styles.skuSidebarSection}>
                            <div className={styles.skuGroupLabel}>Parent Product</div>
                            {parentSkus.map((parentItem, index) => {
                                const isExpanded = expandedParentSku === parentItem.sku;
                                const childrenForParent = childSkus.filter(
                                    (child) =>
                                        child.parentSku &&
                                        String(child.parentSku).trim().toLowerCase() ===
                                            String(parentItem.sku).trim().toLowerCase()
                                );

                                return (
                                    <div key={`parent-${index}`}>
                                        <div
                                            className={`${styles.skuCard} ${
                                                parentItem.sku === selectedSku ? styles.skuCardSelected : ''
                                            }`}
                                            onClick={() => {
                                                handleSkuClick(parentItem.sku);
                                                setExpandedParentSku(isExpanded ? null : parentItem.sku);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleSkuClick(parentItem.sku);
                                                    setExpandedParentSku(isExpanded ? null : parentItem.sku);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className={styles.skuCardImage}>
                                                {getSkuThumbnail(parentItem.sku) ? (
                                                    <img src={getSkuThumbnail(parentItem.sku)} alt={parentItem.sku} />
                                                ) : (
                                                    <div className={styles.skuCardImagePlaceholder} />
                                                )}
                                                {parentItem.sku === selectedSku && (
                                                    <div className={styles.skuCardCheck}><CheckIcon /></div>
                                                )}
                                            </div>
                                            <div className={styles.skuCardContent}>
                                                <div className={styles.skuCardTitle}>{parentItem.title}</div>
                                                <div className={styles.skuCardBadge}>SKU ID : {parentItem.sku}</div>
                                            </div>
                                            {childrenForParent.length > 0 && (
                                                <div className={styles.skuParentArrow}>
                                                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                </div>
                                            )}
                                        </div>

                                        {isExpanded && childrenForParent.length > 0 && (
                                            <div className={`${styles.skuSidebarSection} ${styles.skuSidebarSectionChild}`}>
                                                <div className={styles.skuChildGroupLabel}>Child variants</div>
                                                {childrenForParent.map((childItem, cIndex) => (
                                                    <div key={`child-${parentItem.sku}-${cIndex}`} className={styles.skuChildCardWrapper}>
                                                        <SkuCard
                                                            item={childItem}
                                                            isSelected={childItem.sku === selectedSku}
                                                            isChild
                                                            onClick={() => handleSkuClick(childItem.sku)}
                                                            getSkuThumbnail={getSkuThumbnail}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* If no parent/child distinction, show all */}
                {parentSkus.length === 0 && childSkus.length === 0 && skuList.length > 0 && (
                    <div className={styles.skuSidebarSection}>
                        <div className={styles.skuGroupLabel}>Products</div>
                        {skuList.map((item, index) => (
                            <SkuCard
                                key={index}
                                item={item}
                                isSelected={item.sku === selectedSku}
                                onClick={() => handleSkuClick(item.sku)}
                                getSkuThumbnail={getSkuThumbnail}
                            />
                        ))}
                    </div>
                )}

                {skuList.length === 0 && (
                    <div className={styles.noSkus}>No SKUs found in Excel</div>
                )}

                {isLoadingImages && (
                    <div className={styles.loadingIndicator}>Loading images...</div>
                )}
            </div>
        </div>
    );
};

export default React.memo(SkuSidebar);
